import { fileTypeFromBuffer } from 'file-type';
import { EyeIcon, Trash2Icon } from 'lucide-react';
import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { default as Lightbox } from 'yet-another-react-lightbox';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';

interface Props {
  accept?: string;
  data?: null | string;
  id?: string;
  name?: string;
  onChange?: (base64: null | string, mimeType?: string) => void;
  readOnly?: boolean;
}

const FileInput = ({
  accept,
  data,
  id,
  name,
  onChange,
  readOnly = false
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const lightboxPortalRef = useRef<HTMLDivElement>(null);
  const prevFilesRef = useRef<FileList | null>(null);

  const [fileName, setFileName] = useState<null | string>(null);
  const [lightboxIsOpen, setLightboxIsOpen] = useState(false);
  const [lightboxPortalRoot, setLightboxPortalRoot] =
    useState<HTMLElement | null>(null);
  const [mimeType, setMimeType] = useState('image/png');
  const [localData, setLocalData] = useState<null | string>(data ?? null);
  const hasFile = Boolean(localData);

  useEffect(() => {
    setLightboxPortalRoot(lightboxPortalRef.current);
  }, []);

  useEffect(() => {
    if (!data) {
      queueMicrotask(() => {
        setLocalData(null);
        setFileName(null);
        setMimeType('image/png');
        prevFilesRef.current = null;
        if (inputRef.current) {
          inputRef.current.value = '';
        }
      });
      return;
    }
    queueMicrotask(() => {
      setLocalData(data);
    });

    let isActive = true;
    const bytes = Uint8Array.from(atob(data), c => c.charCodeAt(0));
    void fileTypeFromBuffer(bytes)
      .then(result => {
        if (!isActive) return;
        const ext = result?.ext ?? 'png';
        const mime = result?.mime ?? 'image/png';
        const resolvedFileName = `file.${ext}`;

        setMimeType(mime);
        setFileName(resolvedFileName);

        if (inputRef.current) {
          const file = new File([bytes], resolvedFileName, { type: mime });
          const dt = new DataTransfer();
          dt.items.add(file);
          inputRef.current.files = dt.files;
          prevFilesRef.current = dt.files;
        }
      })
      .catch(() => {
        if (!isActive) return;
        setMimeType('image/png');
        setFileName('file.png');
      });

    return () => {
      isActive = false;
    };
  }, [data]);

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    const handleCancel = () => {
      if (prevFilesRef.current) {
        input.files = prevFilesRef.current;
      }
    };

    input.addEventListener('cancel', handleCancel);
    return () => {
      input.removeEventListener('cancel', handleCancel);
    };
  }, []);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) {
        if (inputRef.current && prevFilesRef.current) {
          inputRef.current.files = prevFilesRef.current;
        }
        return;
      }
      prevFilesRef.current = e.target.files;
      setFileName(file.name);
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        if (result.startsWith('data:')) {
          const [header, payload] = result.split(',');
          const mimeMatch = /data:([^;]+)/.exec(header);
          const mime = mimeMatch ? mimeMatch[1] : file.type || 'image/png';
          setLocalData(payload);
          setMimeType(mime);
          onChange?.(payload, mime);
        } else {
          const mime = file.type || 'image/png';
          setLocalData(result);
          setMimeType(mime);
          onChange?.(result, mime);
        }
      };
      reader.readAsDataURL(file);
    },
    [onChange]
  );

  return (
    <div
      className="flex gap-x-2.5"
      ref={lightboxPortalRef}
    >
      {readOnly ? (
        <Input
          id={id}
          name={name}
          placeholder="No file uploaded"
          readOnly
          value={fileName ?? ''}
        />
      ) : (
        <Input
          accept={accept}
          id={id}
          name={name}
          onChange={handleChange}
          ref={inputRef}
          type="file"
        />
      )}
      {hasFile && (
        <div className="flex gap-x-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={() => {
                  setLightboxIsOpen(true);
                }}
                size="icon"
                type="button"
                variant="outline"
              >
                <EyeIcon />
              </Button>
            </TooltipTrigger>
            <TooltipContent>View File</TooltipContent>
          </Tooltip>
          <Lightbox
            carousel={{ finite: true }}
            close={() => {
              setLightboxIsOpen(false);
            }}
            controller={{ closeOnBackdropClick: true }}
            open={lightboxIsOpen}
            portal={
              lightboxPortalRoot ? { root: lightboxPortalRoot } : undefined
            }
            render={{
              buttonNext: () => null,
              buttonPrev: () => null
            }}
            slides={[
              {
                src: `data:${mimeType};base64,${localData ?? ''}`
              }
            ]}
          />
          {!readOnly && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={() => {
                    if (inputRef.current) {
                      inputRef.current.value = '';
                    }
                    prevFilesRef.current = null;
                    setFileName(null);
                    setLocalData(null);
                    setMimeType('image/png');
                    onChange?.(null);
                  }}
                  size="icon"
                  type="button"
                  variant="destructive"
                >
                  <Trash2Icon />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Delete File</TooltipContent>
            </Tooltip>
          )}
        </div>
      )}
    </div>
  );
};

export default FileInput;
